<template>
  <div class="tab-shell">
    <div class="tab-head">
      <h2 class="tab-title">Centros de Custo</h2>
      <button v-if="!showForm" class="btn-primary" type="button" @click="openForm()">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        Novo Centro de Custo
      </button>
    </div>

    <!-- Project filter -->
    <div class="filter-row">
      <select class="filter-select" v-model="projectFilter">
        <option value="">Todas as obras</option>
        <option v-for="p in projects" :key="p.id" :value="p.id">{{ p.name }}</option>
      </select>
    </div>

    <!-- Form -->
    <div v-if="showForm" class="form-card">
      <h3 class="form-title">{{ editId ? 'Editar Centro de Custo' : 'Novo Centro de Custo' }}</h3>
      <div class="form-grid">
        <label class="field">
          <span class="field-label">Nome *</span>
          <input class="field-input" v-model="form.nome" placeholder="Ex: Estrutura Bloco A" />
        </label>
        <label class="field">
          <span class="field-label">Obra</span>
          <select class="field-input" v-model="form.projectId">
            <option value="">Selecione...</option>
            <option v-for="p in projects" :key="p.id" :value="p.id">{{ p.name }}</option>
          </select>
        </label>
        <label class="field">
          <span class="field-label">Orçamento Total (R$)</span>
          <input class="field-input" type="number" v-model.number="form.orcamentoTotal" min="0" step="0.01" placeholder="0,00" />
        </label>
        <div class="field"><!-- spacer --></div>
        <div class="field--full form-section-title">Breakdown por categoria</div>
        <label class="field">
          <span class="field-label">Mão de Obra (R$)</span>
          <input class="field-input" type="number" v-model.number="form.orcamentoMaoObra" min="0" step="0.01" placeholder="0,00" />
        </label>
        <label class="field">
          <span class="field-label">Materiais (R$)</span>
          <input class="field-input" type="number" v-model.number="form.orcamentoMateriais" min="0" step="0.01" placeholder="0,00" />
        </label>
        <label class="field">
          <span class="field-label">Equipamentos (R$)</span>
          <input class="field-input" type="number" v-model.number="form.orcamentoEquipamentos" min="0" step="0.01" placeholder="0,00" />
        </label>
        <label class="field">
          <span class="field-label">Serviços (R$)</span>
          <input class="field-input" type="number" v-model.number="form.orcamentoServicos" min="0" step="0.01" placeholder="0,00" />
        </label>
        <label class="field">
          <span class="field-label">Outros (R$)</span>
          <input class="field-input" type="number" v-model.number="form.orcamentoOutros" min="0" step="0.01" placeholder="0,00" />
        </label>
      </div>
      <p v-if="formError" class="form-error">{{ formError }}</p>
      <div class="form-actions">
        <button class="btn-save" type="button" :disabled="saving" @click="save">{{ saving ? 'Salvando...' : 'Salvar' }}</button>
        <button class="btn-cancel" type="button" @click="closeForm">Cancelar</button>
      </div>
    </div>

    <!-- List -->
    <div v-if="loading" class="empty-hint">Carregando...</div>
    <div v-else-if="filteredCcs.length === 0 && !showForm" class="empty-hint">
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
      Nenhum centro de custo cadastrado.
    </div>
    <div v-else class="cards-grid">
      <article v-for="cc in filteredCcs" :key="cc.id" class="cc-card">
        <div class="card-body">
          <div class="card-title">{{ cc.nome }}</div>
          <div class="card-meta" v-if="cc.projectName">{{ cc.projectName }}</div>
          <div class="budget-row">
            <span class="budget-label">Previsto:</span>
            <span class="budget-val">{{ formatCurrency(cc.orcamentoTotal) }}</span>
          </div>
          <div class="budget-row" v-if="cc.realizado !== undefined">
            <span class="budget-label">Realizado:</span>
            <span class="budget-val">{{ formatCurrency(cc.realizado) }}</span>
          </div>
          <div class="progress-section" v-if="cc.orcamentoTotal > 0">
            <div class="progress-bar-bg">
              <div
                class="progress-bar-fill"
                :class="progressClass(cc)"
                :style="{ width: progressPct(cc) + '%' }"
              ></div>
            </div>
            <div class="progress-labels">
              <span class="progress-pct" :class="progressClass(cc)">{{ progressPct(cc).toFixed(1) }}%</span>
              <span class="progress-saldo">Saldo: {{ formatCurrency((cc.orcamentoTotal || 0) - (cc.realizado || 0)) }}</span>
            </div>
          </div>
          <div class="breakdown-grid" v-if="hasBreakdown(cc)">
            <div v-if="cc.orcamentoMaoObra" class="breakdown-item"><span class="b-label">Mão de Obra</span><span class="b-val">{{ formatCurrency(cc.orcamentoMaoObra) }}</span></div>
            <div v-if="cc.orcamentoMateriais" class="breakdown-item"><span class="b-label">Materiais</span><span class="b-val">{{ formatCurrency(cc.orcamentoMateriais) }}</span></div>
            <div v-if="cc.orcamentoEquipamentos" class="breakdown-item"><span class="b-label">Equipamentos</span><span class="b-val">{{ formatCurrency(cc.orcamentoEquipamentos) }}</span></div>
            <div v-if="cc.orcamentoServicos" class="breakdown-item"><span class="b-label">Serviços</span><span class="b-val">{{ formatCurrency(cc.orcamentoServicos) }}</span></div>
            <div v-if="cc.orcamentoOutros" class="breakdown-item"><span class="b-label">Outros</span><span class="b-val">{{ formatCurrency(cc.orcamentoOutros) }}</span></div>
          </div>
        </div>
        <div class="card-footer">
          <button class="card-btn" type="button" @click="openForm(cc)">Editar</button>
          <button class="card-btn card-btn--warn" type="button" @click="remove(cc.id)">Excluir</button>
        </div>
      </article>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue'
import { apiFetch } from '@/store/api'
import { uiStore } from '@/store/ui'
import { projectsStore } from '@/store/projects'

interface CentroCusto {
  id: string; nome: string; projectId: string; projectName: string
  orcamentoTotal: number; orcamentoMaoObra: number; orcamentoMateriais: number
  orcamentoEquipamentos: number; orcamentoServicos: number; orcamentoOutros: number
  realizado: number
}

const emptyForm = () => ({
  nome: '', projectId: '', orcamentoTotal: 0, orcamentoMaoObra: 0,
  orcamentoMateriais: 0, orcamentoEquipamentos: 0, orcamentoServicos: 0, orcamentoOutros: 0
})

export default defineComponent({
  name: 'OfficeCentroCustoTab',
  setup() {
    const ccs = ref<CentroCusto[]>([])
    const loading = ref(false)
    const showForm = ref(false)
    const editId = ref('')
    const form = ref(emptyForm())
    const formError = ref('')
    const saving = ref(false)
    const projectFilter = ref('')

    const projects = computed(() => projectsStore.all())

    const filteredCcs = computed(() =>
      projectFilter.value ? ccs.value.filter(c => c.projectId === projectFilter.value) : ccs.value
    )

    const load = async () => {
      loading.value = true
      try { ccs.value = await apiFetch<CentroCusto[]>('/office/cost-centers') }
      catch { ccs.value = [] }
      finally { loading.value = false }
    }

    const openForm = (cc?: CentroCusto) => {
      if (cc) {
        editId.value = cc.id
        form.value = {
          nome: cc.nome, projectId: cc.projectId, orcamentoTotal: cc.orcamentoTotal,
          orcamentoMaoObra: cc.orcamentoMaoObra, orcamentoMateriais: cc.orcamentoMateriais,
          orcamentoEquipamentos: cc.orcamentoEquipamentos, orcamentoServicos: cc.orcamentoServicos,
          orcamentoOutros: cc.orcamentoOutros
        }
      } else { editId.value = ''; form.value = emptyForm() }
      formError.value = ''; showForm.value = true
    }
    const closeForm = () => { showForm.value = false; editId.value = ''; formError.value = '' }

    const save = async () => {
      if (!form.value.nome.trim()) { formError.value = 'Nome é obrigatório.'; return }
      saving.value = true; formError.value = ''
      try {
        if (editId.value) await apiFetch(`/office/cost-centers/${editId.value}`, { method: 'PUT', body: JSON.stringify(form.value) })
        else await apiFetch('/office/cost-centers', { method: 'POST', body: JSON.stringify(form.value) })
        uiStore.toast(editId.value ? 'Centro de custo atualizado.' : 'Centro de custo criado.', 'success')
        closeForm(); await load()
      } catch (e: unknown) { formError.value = e instanceof Error ? e.message : 'Erro ao salvar.' }
      finally { saving.value = false }
    }

    const remove = async (id: string) => {
      const ok = await uiStore.confirm('Excluir este centro de custo?')
      if (!ok) return
      try {
        await apiFetch(`/office/cost-centers/${id}`, { method: 'DELETE' })
        uiStore.toast('Centro de custo excluído.', 'success'); await load()
      } catch (e: unknown) { uiStore.toast(e instanceof Error ? e.message : 'Erro ao excluir.', 'error') }
    }

    const progressPct = (cc: CentroCusto) => {
      if (!cc.orcamentoTotal) return 0
      return Math.min(((cc.realizado || 0) / cc.orcamentoTotal) * 100, 100)
    }
    const progressClass = (cc: CentroCusto) => {
      const pct = (cc.realizado || 0) / (cc.orcamentoTotal || 1) * 100
      if (pct >= 100) return 'prog--red'
      if (pct >= 80) return 'prog--yellow'
      return 'prog--green'
    }
    const hasBreakdown = (cc: CentroCusto) =>
      cc.orcamentoMaoObra || cc.orcamentoMateriais || cc.orcamentoEquipamentos || cc.orcamentoServicos || cc.orcamentoOutros

    const formatCurrency = (v: number) =>
      new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)

    onMounted(() => {
      load()
      if (!projectsStore.all().length) projectsStore.refresh().catch(() => { /* ignore */ })
    })

    return {
      ccs, projects, loading, showForm, editId, form, formError, saving,
      projectFilter, filteredCcs, openForm, closeForm, save, remove,
      progressPct, progressClass, hasBreakdown, formatCurrency
    }
  }
})
</script>

<style scoped lang="scss">
.tab-shell { width: 100%; }
.tab-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.tab-title { margin: 0; font-size: 18px; font-weight: 800; color: var(--ink, #2c2b27); }
.btn-primary { display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 8px; border: none; cursor: pointer; background: var(--system-navbar, #0f4c81); color: var(--system-navbar-text, #fff); font-size: 13px; font-weight: 600; &:hover { opacity: .88; } }
.filter-row { margin-bottom: 20px; }
.filter-select { padding: 7px 12px; border-radius: 7px; border: 1.5px solid rgba(31,30,26,.15); font-size: 13px; outline: none; background: #fff; min-width: 200px; &:focus { border-color: var(--system-navbar, #0f4c81); } }
.form-card { background: var(--card, #fff); border: 1px solid rgba(31,30,26,.1); border-radius: 14px; padding: 20px; margin-bottom: 24px; }
.form-title { margin: 0 0 16px; font-size: 15px; font-weight: 700; color: var(--ink, #2c2b27); }
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.field { display: flex; flex-direction: column; gap: 4px; }
.field--full { grid-column: 1 / -1; }
.field-label { font-size: 12px; font-weight: 600; color: var(--ink-muted, #888); }
.field-input { padding: 8px 11px; border-radius: 7px; border: 1.5px solid rgba(31,30,26,.18); font-size: 13px; outline: none; background: #fff; &:focus { border-color: var(--system-navbar, #0f4c81); } }
.form-section-title { font-size: 12px; font-weight: 700; color: var(--ink-muted, #777); text-transform: uppercase; letter-spacing: .04em; padding-top: 4px; }
.form-error { font-size: 12px; color: #dc2626; margin: 8px 0 0; }
.form-actions { display: flex; gap: 8px; margin-top: 16px; }
.btn-save { padding: 8px 18px; border-radius: 7px; border: none; cursor: pointer; background: var(--system-navbar, #0f4c81); color: #fff; font-size: 13px; font-weight: 600; &:disabled { opacity: .6; cursor: not-allowed; } &:hover:not(:disabled) { opacity: .88; } }
.btn-cancel { padding: 8px 14px; border-radius: 7px; border: 1.5px solid rgba(31,30,26,.18); background: #fff; cursor: pointer; font-size: 13px; font-weight: 500; &:hover { background: rgba(31,30,26,.04); } }
.empty-hint { display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 60px 20px; color: var(--ink-muted, #aaa); text-align: center; font-size: 14px; svg { opacity: .35; } }
.cards-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 14px; }
.cc-card { border: 1px solid rgba(31,30,26,.1); border-radius: 14px; background: var(--card, #fff); display: flex; flex-direction: column; }
.card-body { padding: 14px 16px 10px; flex: 1; display: flex; flex-direction: column; gap: 6px; }
.card-title { font-size: 15px; font-weight: 700; color: var(--ink, #2c2b27); }
.card-meta { font-size: 12px; color: var(--ink-muted, #888); }
.budget-row { display: flex; justify-content: space-between; align-items: center; }
.budget-label { font-size: 12px; color: var(--ink-muted, #888); }
.budget-val { font-size: 13px; font-weight: 600; color: var(--ink, #2c2b27); }
.progress-section { display: flex; flex-direction: column; gap: 4px; }
.progress-bar-bg { height: 8px; border-radius: 4px; background: rgba(31,30,26,.1); overflow: hidden; }
.progress-bar-fill { height: 100%; border-radius: 4px; transition: width .3s; &.prog--green { background: #22c55e; } &.prog--yellow { background: #eab308; } &.prog--red { background: #ef4444; } }
.progress-labels { display: flex; justify-content: space-between; }
.progress-pct { font-size: 11px; font-weight: 700; &.prog--green { color: #15803d; } &.prog--yellow { color: #854d0e; } &.prog--red { color: #dc2626; } }
.progress-saldo { font-size: 11px; color: var(--ink-muted, #888); }
.breakdown-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px; border-top: 1px solid rgba(31,30,26,.07); padding-top: 8px; margin-top: 4px; }
.breakdown-item { display: flex; flex-direction: column; gap: 1px; }
.b-label { font-size: 10px; color: var(--ink-muted, #aaa); }
.b-val { font-size: 12px; font-weight: 600; color: var(--ink, #2c2b27); }
.card-footer { display: flex; gap: 2px; padding: 8px 10px; border-top: 1px solid rgba(31,30,26,.07); }
.card-btn { display: inline-flex; align-items: center; padding: 5px 9px; border-radius: 6px; border: none; cursor: pointer; background: transparent; color: var(--ink-muted, #666); font-size: 11px; font-weight: 500; transition: background .12s; &:hover { background: rgba(31,30,26,.06); color: var(--ink, #2c2b27); } &--warn:hover { background: #fff7ed; color: #c2410c; } }
@media (max-width: 600px) { .form-grid { grid-template-columns: 1fr; } .cards-grid { grid-template-columns: 1fr; } }
</style>
